import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { NodeListComponent } from "./components/nodeList/nodeListComponent";
import { PropertyTabComponent } from "./components/propertyTab/propertyTabComponent";
import { Portal } from "./portal";
import { LogComponent } from "./components/log/logComponent";
import { DataStorage } from "core/Misc/dataStorage";
import { MessageDialog } from "shared-ui-components/components/MessageDialog";
import { BlockTools } from "./blockTools";
import { PreviewManager } from "./components/preview/previewManager";
import { PreviewMeshControlComponent } from "./components/preview/previewMeshControlComponent";
import { PreviewAreaComponent } from "./components/preview/previewAreaComponent";
import { SerializationTools } from "./serializationTools";
import { createRoot } from "react-dom/client";
import { CreatePopup } from "shared-ui-components/popupHelper";
import "./main.scss";
import { GraphCanvasComponent } from "shared-ui-components/nodeGraphSystem/graphCanvas";
import { TypeLedger } from "shared-ui-components/nodeGraphSystem/typeLedger";
import { HistoryStack } from "shared-ui-components/historyStack";
import { SplitContainer } from "shared-ui-components/split/splitContainer";
import { Splitter } from "shared-ui-components/split/splitter";
import { ControlledSize, SplitDirection } from "shared-ui-components/split/splitContext";
import { NodeParticleBlock } from "core/Particles/Node/nodeParticleBlock";
import { ParticleInputBlock } from "core/Particles/Node/Blocks/particleInputBlock";
export class GraphEditor extends React.Component {
    appendBlock(dataToAppend, recursion = true) {
        return this._graphCanvas.createNodeFromObject(dataToAppend instanceof NodeParticleBlock ? TypeLedger.NodeDataBuilder(dataToAppend, this._graphCanvas) : dataToAppend, (block) => {
            const set = this.props.globalState.nodeParticleSet;
            if (set.attachedBlocks.indexOf(block) === -1) {
                set.attachedBlocks.push(block);
            }
            if (block.isSystem && set.systemBlocks.indexOf(block) === -1) {
                // If the block is a system block, we need to add it to the particle set
                set.systemBlocks.push(block);
            }
        }, recursion);
    }
    addValueNode(type) {
        const nodeType = BlockTools.GetConnectionNodeTypeFromString(type);
        const newInputBlock = new ParticleInputBlock(type, nodeType);
        return this.appendBlock(newInputBlock);
    }
    prepareHistoryStack() {
        const particleSet = this.props.globalState.nodeParticleSet;
        const globalState = this.props.globalState;
        const dataProvider = () => {
            SerializationTools.UpdateLocations(particleSet, globalState);
            return particleSet.serialize();
        };
        const applyUpdate = (data) => {
            globalState.stateManager.onSelectionChangedObservable.notifyObservers(null);
            particleSet.parseSerializedObject(data);
            globalState.onResetRequiredObservable.notifyObservers(false);
        };
        // Create the stack
        this._historyStack = new HistoryStack(dataProvider, applyUpdate);
        this._historyStack.isEnabled = DataStorage.ReadBoolean("UndoRedo", true);
        globalState.stateManager.historyStack = this._historyStack;
        // Connect to relevant events
        globalState.stateManager.onUpdateRequiredObservable.add(() => {
            void this._historyStack.storeAsync();
        });
        globalState.stateManager.onRebuildRequiredObservable.add(() => {
            void this._historyStack.storeAsync();
        });
        globalState.stateManager.onNodeMovedObservable.add(() => {
            void this._historyStack.storeAsync();
        });
        globalState.stateManager.onNewNodeCreatedObservable.add(() => {
            void this._historyStack.storeAsync();
        });
        globalState.onClearUndoStack.add(() => {
            this._historyStack.reset();
        });
    }
    componentDidMount() {
        window.addEventListener("wheel", this.onWheel, { passive: false });
        if (this.props.globalState.hostDocument) {
            this._graphCanvas = this._graphCanvasRef.current;
            this.prepareHistoryStack();
            this._previewManager = new PreviewManager(this.props.globalState.hostDocument.getElementById("preview-canvas"), this.props.globalState);
            this.props.globalState._previewManager = this._previewManager;
        }
        this.props.globalState.onPopupClosedObservable.addOnce(() => {
            this.componentWillUnmount();
        });
        this.build();
        this.props.globalState.onClearUndoStack.notifyObservers();
    }
    componentWillUnmount() {
        window.removeEventListener("wheel", this.onWheel);
        const globalState = this.props.globalState;
        if (globalState.hostDocument) {
            globalState.hostDocument.removeEventListener("keyup", this._onWidgetKeyUpPointer, false);
        }
        globalState.stateManager.onUpdateRequiredObservable.clear();
        globalState.stateManager.onRebuildRequiredObservable.clear();
        globalState.stateManager.onNodeMovedObservable.clear();
        globalState.stateManager.onNewNodeCreatedObservable.clear();
        globalState.onClearUndoStack.clear();
        if (this._historyStack) {
            this._historyStack.dispose();
            this._historyStack = null;
        }
        if (this._previewManager) {
            this._previewManager.dispose();
            this._previewManager = null;
        }
    }
    constructor(props) {
        super(props);
        this._mouseLocationX = 0;
        this._mouseLocationY = 0;
        this.onWheel = (evt) => {
            if (this.props.globalState.pointerOverCanvas) {
                return evt.preventDefault();
            }
            if (evt.ctrlKey) {
                return evt.preventDefault();
            }
            if (Math.abs(evt.deltaX) < Math.abs(evt.deltaY)) {
                return;
            }
            const targetElem = evt.currentTarget;
            const scrollLeftMax = targetElem.scrollWidth - targetElem.offsetWidth;
            if (targetElem.scrollLeft + evt.deltaX < 0 || targetElem.scrollLeft + evt.deltaX > scrollLeftMax) {
                return evt.preventDefault();
            }
        };
        this.handlePopUp = () => {
            this.setState({
                showPreviewPopUp: true,
            });
            this.createPopUp();
            this.props.globalState.hostWindow.addEventListener("beforeunload", this.handleClosingPopUp);
        };
        this.handleClosingPopUp = () => {
            if (this._previewManager) {
                this._previewManager.dispose();
            }
            this._popUpWindow?.close();
            this.setState({
                showPreviewPopUp: false,
            }, () => this.initiatePreviewArea());
        };
        this.initiatePreviewArea = (canvas = this.props.globalState.hostDocument.getElementById("preview-canvas")) => {
            this._previewManager = new PreviewManager(canvas, this.props.globalState);
        };
        this.createPopUp = () => {
            const userOptions = {
                original: true,
                popup: true,
                overlay: false,
                embedMode: false,
                enableClose: true,
                handleResize: true,
                enablePopup: true,
            };
            const options = {
                embedHostWidth: "100%",
                ...userOptions,
            };
            let popUpWindow = null;
            CreatePopup("PREVIEW AREA", {
                width: 500,
                height: 500,
                onParentControlCreateCallback: (parentControl) => {
                    parentControl.style.display = "grid";
                    parentControl.style.gridTemplateRows = "40px auto";
                    parentControl.id = "node-particle-editor-graph-root";
                    parentControl.className = "nge-right-panel popup";
                },
                // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-misused-promises
                onWindowCreateCallback: async (w) => {
                    popUpWindow = w;
                    if (popUpWindow) {
                        popUpWindow.addEventListener("beforeunload", this.handleClosingPopUp);
                        const parentControl = popUpWindow.document.getElementById("node-particle-editor-graph-root");
                        await this.createPreviewMeshControlHostAsync(options, parentControl);
                        await this.createPreviewHostAsync(options, parentControl);
                        if (parentControl) {
                            this.fixPopUpStyles(parentControl.ownerDocument);
                            this.initiatePreviewArea(parentControl.ownerDocument.getElementById("preview-canvas"));
                        }
                    }
                },
            });
        };
        this.createPreviewMeshControlHostAsync = async (options, parentControl) => {
            // Prepare the preview control host
            return await new Promise((resolve) => {
                if (parentControl) {
                    const host = parentControl.ownerDocument.createElement("div");
                    host.id = "PreviewMeshControl-host";
                    host.style.width = options.embedHostWidth || "auto";
                    parentControl.appendChild(host);
                    const previewMeshControlComponentHost = React.createElement(PreviewMeshControlComponent, {
                        globalState: this.props.globalState,
                        togglePreviewAreaComponent: this.handlePopUp,
                        onMounted: () => {
                            resolve(true);
                        },
                    });
                    const root = createRoot(host);
                    root.render(previewMeshControlComponentHost);
                }
                resolve(false);
            });
        };
        this.createPreviewHostAsync = async (options, parentControl) => {
            return await new Promise((resolve) => {
                // Prepare the preview host
                if (parentControl) {
                    const host = parentControl.ownerDocument.createElement("div");
                    host.id = "PreviewAreaComponent-host";
                    host.style.width = options.embedHostWidth || "auto";
                    host.style.height = "100%";
                    host.style.overflow = "hidden";
                    host.style.display = "grid";
                    host.style.gridRow = "2";
                    host.style.gridTemplateRows = "auto 40px";
                    host.style.gridTemplateRows = "calc(100% - 40px) 40px";
                    parentControl.appendChild(host);
                    this._previewHost = host;
                    if (!options.overlay) {
                        this._previewHost.style.position = "relative";
                    }
                }
                if (this._previewHost) {
                    const previewAreaComponentHost = React.createElement(PreviewAreaComponent, {
                        globalState: this.props.globalState,
                        onMounted: () => {
                            resolve(true);
                        },
                    });
                    const root = createRoot(this._previewHost);
                    root.render(previewAreaComponentHost);
                }
                resolve(false);
            });
        };
        this.fixPopUpStyles = (document) => {
            const previewContainer = document.getElementById("preview");
            if (previewContainer) {
                previewContainer.style.height = "auto";
                previewContainer.style.gridRow = "1";
                previewContainer.style.aspectRatio = "unset";
            }
            const previewConfigBar = document.getElementById("preview-config-bar");
            if (previewConfigBar) {
                previewConfigBar.style.gridRow = "2";
            }
            const newWindowButton = document.getElementById("preview-new-window");
            if (newWindowButton) {
                newWindowButton.style.display = "none";
            }
            const previewMeshBar = document.getElementById("preview-mesh-bar");
            if (previewMeshBar) {
                previewMeshBar.style.gridTemplateColumns = "auto 1fr 40px 40px";
            }
        };
        this.state = {
            showPreviewPopUp: false,
            message: "",
            isError: true,
        };
        this._graphCanvasRef = React.createRef();
        this._diagramContainerRef = React.createRef();
        this.props.globalState.stateManager.onNewBlockRequiredObservable.add((eventData) => {
            let targetX = eventData.targetX;
            let targetY = eventData.targetY;
            if (eventData.needRepositioning) {
                const container = this._diagramContainerRef.current;
                targetX = targetX - container.offsetLeft;
                targetY = targetY - container.offsetTop;
            }
            const selectedLink = this._graphCanvas.selectedLink;
            const selectedNode = this._graphCanvas.selectedNodes.length ? this._graphCanvas.selectedNodes[0] : null;
            const newNode = this.emitNewBlock(eventData.type, targetX, targetY);
            if (newNode && eventData.smartAdd) {
                if (selectedLink) {
                    this._graphCanvas.smartAddOverLink(newNode, selectedLink);
                }
                else if (selectedNode) {
                    this._graphCanvas.smartAddOverNode(newNode, selectedNode);
                }
            }
        });
        this.props.globalState.stateManager.onRebuildRequiredObservable.add(() => {
            if (this.props.globalState.nodeParticleSet) {
                this.buildParticleSystemSet();
            }
        });
        this.props.globalState.onResetRequiredObservable.add((isDefault) => {
            if (isDefault) {
                if (this.props.globalState.nodeParticleSet) {
                    this.buildParticleSystemSet();
                }
                this.build(true);
            }
            else {
                this.build();
                if (this.props.globalState.nodeParticleSet) {
                    this.buildParticleSystemSet();
                }
            }
        });
        this.props.globalState.onImportFrameObservable.add((source) => {
            const frameData = source.editorData.frames[0];
            // create new graph nodes for only blocks from frame (last blocks added)
            const blocks = this.props.globalState.nodeParticleSet.attachedBlocks.slice(-frameData.blocks.length);
            for (const block of blocks) {
                this.appendBlock(block);
            }
            this._graphCanvas.addFrame(frameData);
            this.reOrganize(this.props.globalState.nodeParticleSet.editorData, true);
        });
        this.props.globalState.onZoomToFitRequiredObservable.add(() => {
            this.zoomToFit();
        });
        this.props.globalState.onReOrganizedRequiredObservable.add(() => {
            this.reOrganize();
        });
        this.props.globalState.onGetNodeFromBlock = (block) => {
            return this._graphCanvas.findNodeFromData(block);
        };
        this.props.globalState.hostDocument.addEventListener("keydown", (evt) => {
            if (this._historyStack.processKeyEvent(evt)) {
                return;
            }
            void this._graphCanvas.handleKeyDownAsync(evt, (nodeData) => {
                this.props.globalState.nodeParticleSet.removeBlock(nodeData.data);
            }, this._mouseLocationX, this._mouseLocationY, async (nodeData) => {
                const block = nodeData.data;
                const clone = block.clone();
                if (!clone) {
                    return null;
                }
                return this.appendBlock(clone, false);
            }, this.props.globalState.hostDocument.querySelector(".diagram-container"));
        });
        this.props.globalState.stateManager.onErrorMessageDialogRequiredObservable.add((message) => {
            this.setState({ message: message, isError: true });
        });
    }
    zoomToFit() {
        this._graphCanvas.zoomToFit();
    }
    buildParticleSystemSet() {
        if (!this.props.globalState.nodeParticleSet) {
            return;
        }
        this.props.globalState.onBuildRequiredObservable.notifyObservers();
        SerializationTools.UpdateLocations(this.props.globalState.nodeParticleSet, this.props.globalState);
    }
    build(ignoreEditorData = false) {
        let editorData = ignoreEditorData ? null : this.props.globalState.nodeParticleSet.editorData;
        this._graphCanvas._isLoading = true; // Will help loading large graphes
        if (editorData instanceof Array) {
            editorData = {
                locations: editorData,
            };
        }
        // setup the diagram model
        this._graphCanvas.reset();
        // Load graph of nodes from the particle set
        if (this.props.globalState.nodeParticleSet) {
            this.loadGraph();
        }
        this.reOrganize(editorData);
    }
    loadGraph() {
        const nodeParticleSet = this.props.globalState.nodeParticleSet;
        for (const system of nodeParticleSet.systemBlocks) {
            this.appendBlock(system, true);
        }
        for (const n of nodeParticleSet.attachedBlocks) {
            this.appendBlock(n, true);
        }
        // Links
        for (const n of nodeParticleSet.attachedBlocks) {
            if (n.inputs.length) {
                const nodeData = this._graphCanvas.findNodeFromData(n);
                for (const input of nodeData.content.inputs) {
                    if (input.isConnected) {
                        this._graphCanvas.connectPorts(input.connectedPort, input);
                    }
                }
            }
        }
    }
    showWaitScreen() {
        this.props.globalState.hostDocument.querySelector(".wait-screen")?.classList.remove("hidden");
    }
    hideWaitScreen() {
        this.props.globalState.hostDocument.querySelector(".wait-screen")?.classList.add("hidden");
    }
    reOrganize(editorData = null, isImportingAFrame = false) {
        this.showWaitScreen();
        this._graphCanvas._isLoading = true; // Will help loading large graphes
        this._graphCanvas.reOrganize(editorData, isImportingAFrame);
        this.hideWaitScreen();
    }
    emitNewBlock(blockType, targetX, targetY) {
        let newNode;
        let customBlockData;
        // Dropped something that is not a node
        if (blockType === "") {
            return;
        }
        if (blockType.indexOf("CustomBlock") > -1) {
            const storageData = localStorage.getItem(blockType);
            if (!storageData) {
                this.props.globalState.stateManager.onErrorMessageDialogRequiredObservable.notifyObservers(`Error loading custom block`);
                return;
            }
            customBlockData = JSON.parse(storageData);
            if (!customBlockData) {
                this.props.globalState.stateManager.onErrorMessageDialogRequiredObservable.notifyObservers(`Error parsing custom block`);
                return;
            }
        }
        else if (blockType.indexOf("Custom") > -1) {
            const storageData = localStorage.getItem(blockType);
            if (storageData) {
                const frameData = JSON.parse(storageData);
                //edit position before loading.
                const newX = (targetX - this._graphCanvas.x - GraphCanvasComponent.NodeWidth) / this._graphCanvas.zoom;
                const newY = (targetY - this._graphCanvas.y - 20) / this._graphCanvas.zoom;
                const oldX = frameData.editorData.frames[0].x;
                const oldY = frameData.editorData.frames[0].y;
                frameData.editorData.frames[0].x = newX;
                frameData.editorData.frames[0].y = newY;
                for (const location of frameData.editorData.locations) {
                    location.x += newX - oldX;
                    location.y += newY - oldY;
                }
                SerializationTools.AddFrameToParticleSystemSet(frameData, this.props.globalState, this.props.globalState.nodeParticleSet);
                this._graphCanvas.frames[this._graphCanvas.frames.length - 1].cleanAccumulation();
                this.forceUpdate();
                return;
            }
        }
        if (blockType.indexOf("Block") === -1) {
            newNode = this.addValueNode(blockType);
        }
        else {
            const block = BlockTools.GetBlockFromString(blockType);
            newNode = this.appendBlock(block);
            newNode.addClassToVisual(block.getClassName());
        }
        // Size exceptions
        let offsetX = GraphCanvasComponent.NodeWidth;
        let offsetY = 20;
        if (blockType === "ElbowBlock" || blockType === "DebugBlock") {
            offsetX = 10;
            offsetY = 10;
        }
        // Drop
        this._graphCanvas.drop(newNode, targetX, targetY, offsetX, offsetY);
        this.forceUpdate();
        return newNode;
    }
    dropNewBlock(event) {
        const data = event.dataTransfer.getData("babylonjs-particle-node");
        const container = this._diagramContainerRef.current;
        const rect = container.getBoundingClientRect();
        const targetX = event.clientX - rect.left;
        const targetY = event.clientY - rect.top;
        this.emitNewBlock(data, targetX, targetY);
    }
    render() {
        return (_jsxs(Portal, { globalState: this.props.globalState, children: [_jsxs(SplitContainer, { id: "node-particle-editor-graph-root", direction: SplitDirection.Horizontal, onPointerMove: (evt) => {
                        this._mouseLocationX = evt.pageX;
                        this._mouseLocationY = evt.pageY;
                    }, onPointerDown: (evt) => {
                        if (evt.target.nodeName === "INPUT") {
                            return;
                        }
                        this.props.globalState.lockObject.lock = false;
                    }, children: [_jsx(NodeListComponent, { globalState: this.props.globalState }), _jsx(Splitter, { size: 8, minSize: 180, initialSize: 200, maxSize: 350, controlledSide: ControlledSize.First }), _jsxs(SplitContainer, { direction: SplitDirection.Vertical, className: "diagram-container", containerRef: this._diagramContainerRef, onDrop: (event) => {
                                this.dropNewBlock(event);
                            }, onDragOver: (event) => {
                                event.preventDefault();
                            }, children: [_jsx(GraphCanvasComponent, { ref: this._graphCanvasRef, stateManager: this.props.globalState.stateManager, onEmitNewNode: (nodeData) => {
                                        return this.appendBlock(nodeData.data);
                                    } }), _jsx(Splitter, { size: 8, minSize: 40, initialSize: 120, maxSize: 500, controlledSide: ControlledSize.Second }), _jsx(LogComponent, { globalState: this.props.globalState })] }), _jsx(Splitter, { size: 8, minSize: 250, initialSize: 300, maxSize: 500, controlledSide: ControlledSize.Second }), _jsxs(SplitContainer, { className: "npe-right-panel", direction: SplitDirection.Vertical, children: [_jsx(PropertyTabComponent, { lockObject: this.props.globalState.lockObject, globalState: this.props.globalState }), _jsx(Splitter, { size: 8, minSize: 200, initialSize: 300, maxSize: 500, controlledSide: ControlledSize.Second }), _jsxs("div", { className: "npe-preview-part", children: [!this.state.showPreviewPopUp ? (_jsx(PreviewMeshControlComponent, { globalState: this.props.globalState, togglePreviewAreaComponent: this.handlePopUp })) : null, !this.state.showPreviewPopUp ? _jsx(PreviewAreaComponent, { globalState: this.props.globalState }) : null] })] })] }), _jsx(MessageDialog, { message: this.state.message, isError: this.state.isError, onClose: () => this.setState({ message: "" }) }), _jsx("div", { className: "blocker", children: "Node Particle Editor needs a horizontal resolution of at least 900px" }), _jsx("div", { className: "wait-screen hidden", children: "Processing...please wait" })] }));
    }
}
//# sourceMappingURL=graphEditor.js.map